/**
 * Dismissed Insights hook
 *
 * Provides server-side management of dismissed insight IDs.
 * Enables cross-device sync for dismissed insights.
 *
 * Features:
 * - Fetches dismissed insight IDs from server
 * - Dismiss insights (persisted server-side)
 * - Restore dismissed insights
 * - Clear all dismissed insights
 * - Optimistic updates for better UX
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createApiClient,
  ApiClientError,
  type DismissedInsightsResponse,
} from "@floline/api-client";
import { API_URL } from "@/env";
import { insightsKeys } from "./use-insights";

/** Query key factory for dismissed insights */
export const dismissedInsightsKeys = {
  all: ["dismissed-insights"] as const,
  list: () => [...dismissedInsightsKeys.all, "list"] as const,
};

/** API client configured for dismissed insights requests */
const apiClient = createApiClient(
  API_URL
);

/**
 * Fetch dismissed insight IDs from the API
 */
async function fetchDismissedInsights(): Promise<DismissedInsightsResponse> {
  return apiClient.listDismissedInsights();
}

/**
 * Options for useDismissedInsights hook
 */
export interface UseDismissedInsightsOptions {
  /** Whether the query is enabled (default: true) */
  enabled?: boolean;
}

/**
 * Hook to fetch and manage dismissed insight IDs
 *
 * Returns dismissed insight IDs along with loading and error states,
 * plus mutation functions for dismissing/restoring insights.
 *
 * @example
 * ```tsx
 * function InsightsPage() {
 *   const {
 *     dismissedIds,
 *     isLoading,
 *     dismissInsight,
 *     isDismissing,
 *   } = useDismissedInsights();
 *
 *   const handleDismiss = (insight: Insight) => {
 *     dismissInsight(insight.id);
 *   };
 *
 *   // Filter out dismissed insights
 *   const visibleInsights = insights.filter(
 *     (i) => !dismissedIds.includes(i.id)
 *   );
 *
 *   return <InsightList insights={visibleInsights} onDismiss={handleDismiss} />;
 * }
 * ```
 */
export function useDismissedInsights(options: UseDismissedInsightsOptions = {}) {
  const { enabled = true } = options;
  const queryClient = useQueryClient();

  // Fetch dismissed insight IDs
  const query = useQuery({
    queryKey: dismissedInsightsKeys.list(),
    queryFn: fetchDismissedInsights,
    enabled,
    // Dismissed insights don't change frequently
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    // Retry on errors (except auth errors)
    retry: (failureCount: number, error: Error) => {
      if (error instanceof ApiClientError && error.status === 401) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Dismiss an insight mutation
  const dismissMutation = useMutation({
    mutationFn: (insightId: string) => apiClient.dismissInsight(insightId),
    onMutate: async (insightId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: dismissedInsightsKeys.list(),
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<DismissedInsightsResponse>(
        dismissedInsightsKeys.list()
      );

      // Optimistically update the cache
      if (previousData) {
        queryClient.setQueryData<DismissedInsightsResponse>(
          dismissedInsightsKeys.list(),
          {
            ...previousData,
            data: {
              dismissedInsightIds: [
                ...previousData.data.dismissedInsightIds,
                insightId,
              ],
              count: previousData.data.count + 1,
            },
          }
        );
      }

      return { previousData };
    },
    onError: (_err, _insightId, context) => {
      // Roll back on error
      if (context?.previousData) {
        queryClient.setQueryData(
          dismissedInsightsKeys.list(),
          context.previousData
        );
      }
    },
    onSettled: () => {
      // Invalidate to ensure consistency
      queryClient.invalidateQueries({
        queryKey: dismissedInsightsKeys.list(),
      });
    },
  });

  // Restore a dismissed insight mutation
  const restoreMutation = useMutation({
    mutationFn: (insightId: string) =>
      apiClient.restoreDismissedInsight(insightId),
    onMutate: async (insightId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: dismissedInsightsKeys.list(),
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<DismissedInsightsResponse>(
        dismissedInsightsKeys.list()
      );

      // Optimistically update the cache
      if (previousData) {
        const newIds = previousData.data.dismissedInsightIds.filter(
          (id) => id !== insightId
        );
        queryClient.setQueryData<DismissedInsightsResponse>(
          dismissedInsightsKeys.list(),
          {
            ...previousData,
            data: {
              dismissedInsightIds: newIds,
              count: newIds.length,
            },
          }
        );
      }

      return { previousData };
    },
    onError: (_err, _insightId, context) => {
      // Roll back on error
      if (context?.previousData) {
        queryClient.setQueryData(
          dismissedInsightsKeys.list(),
          context.previousData
        );
      }
    },
    onSettled: () => {
      // Invalidate to ensure consistency
      queryClient.invalidateQueries({
        queryKey: dismissedInsightsKeys.list(),
      });
    },
  });

  // Clear all dismissed insights mutation
  const clearMutation = useMutation({
    mutationFn: () => apiClient.clearDismissedInsights(),
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: dismissedInsightsKeys.list(),
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<DismissedInsightsResponse>(
        dismissedInsightsKeys.list()
      );

      // Optimistically clear the cache
      if (previousData) {
        queryClient.setQueryData<DismissedInsightsResponse>(
          dismissedInsightsKeys.list(),
          {
            ...previousData,
            data: {
              dismissedInsightIds: [],
              count: 0,
            },
          }
        );
      }

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      // Roll back on error
      if (context?.previousData) {
        queryClient.setQueryData(
          dismissedInsightsKeys.list(),
          context.previousData
        );
      }
    },
    onSettled: () => {
      // Invalidate to ensure consistency with server
      queryClient.invalidateQueries({
        queryKey: dismissedInsightsKeys.list(),
      });
      // Also invalidate insights since visible insights will change
      queryClient.invalidateQueries({
        queryKey: insightsKeys.all,
      });
    },
  });

  return {
    /** Array of dismissed insight IDs */
    dismissedIds: query.data?.data.dismissedInsightIds ?? [],
    /** Total count of dismissed insights */
    count: query.data?.data.count ?? 0,
    /** Whether dismissed insights are being fetched */
    isLoading: query.isLoading,
    /** Whether the initial fetch is in progress */
    isPending: query.isPending,
    /** Whether the fetch failed with an error */
    isError: query.isError,
    /** Error details if the fetch failed */
    error: query.error,
    /** Refetch the dismissed insights */
    refetch: query.refetch,

    /** Dismiss an insight by ID */
    dismissInsight: dismissMutation.mutate,
    /** Whether a dismiss operation is in progress */
    isDismissing: dismissMutation.isPending,
    /** Error from dismiss operation */
    dismissError: dismissMutation.error,

    /** Restore a dismissed insight by ID */
    restoreInsight: restoreMutation.mutate,
    /** Whether a restore operation is in progress */
    isRestoring: restoreMutation.isPending,
    /** Error from restore operation */
    restoreError: restoreMutation.error,

    /** Clear all dismissed insights */
    clearDismissed: clearMutation.mutate,
    /** Whether a clear operation is in progress */
    isClearing: clearMutation.isPending,
    /** Error from clear operation */
    clearError: clearMutation.error,
  };
}

/**
 * Helper function to check if an insight is dismissed
 */
export function isInsightDismissed(
  insightId: string,
  dismissedIds: string[]
): boolean {
  return dismissedIds.includes(insightId);
}

/**
 * Helper function to filter out dismissed insights
 */
export function filterDismissedInsights<T extends { id: string }>(
  insights: T[],
  dismissedIds: string[]
): T[] {
  return insights.filter((insight) => !dismissedIds.includes(insight.id));
}
